var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import { useTheme } from '../../theme';
import { useMemoClasses, useMultipleMemoClasses } from '../../utils/hooks';
var useStyles = createUseStyles({
    base: {
        position: 'relative',
        font: 'var(--RUI-font-input)',
        margin: 'var(--RUI-margin)',
        display: 'inline-flex',
        flexDirection: 'column',
        padding: 'calc(0.2 * var(--RUI-padding))',
        paddingBottom: 0,
    },
    baseOverrides: function (theme) { return (__assign({}, theme.overrides.TextArea.base)); },
    filled: {
        borderRadius: '4px 4px 0 0',
        backgroundColor: 'var(--RUI-color-paper-dark)',
    },
    filledOverrides: function (theme) { return (__assign({}, theme.overrides.TextArea.filled)); },
    textArea: {
        order: 2,
        boxSizing: 'border-box',
        width: '100%',
        font: 'inherit',
        border: 0,
        borderBottom: 'var(--RUI-border-width) solid black',
        appearance: 'none',
        cursor: 'text',
        background: 'transparent',
        '&:hover:not(:disabled)': {
            paddingBottom: 'calc(0.3125 * var(--RUI-padding) - var(--RUI-border-width))',
            borderBottomWidth: 'calc(2 * var(--RUI-border-width))',
        },
        '&:focus:not(:disabled), &:active:not(:disabled)': {
            outline: 0,
            paddingBottom: 'calc(0.3125 * var(--RUI-padding) - var(--RUI-border-width))',
            borderBottomWidth: 'calc(2 * var(--RUI-border-width))',
            borderColor: 'var(--RUI-text-area-color)',
            '& + $label': {
                color: 'var(--RUI-text-area-color)',
            },
        },
    },
    textAreaOverrides: function (theme) { return (__assign({}, theme.overrides.TextArea.textArea)); },
    label: {
        font: 'inherit',
        width: 'fit-content',
        pointerEvents: 'none',
        fontSize: '0.8em',
        opacity: '0.75',
    },
    labelOverrides: function (theme) { return (__assign({}, theme.overrides.TextArea.label)); },
    primary: { '--RUI-text-area-color': 'var(--RUI-color-primary)' },
    secondary: { '--RUI-text-area-color': 'var(--RUI-color-secondary)' },
    neutral: { '--RUI-text-area-color': 'var(--RUI-color-neutral)' },
    error: { '--RUI-text-area-color': 'var(--RUI-color-error)' },
    warning: { '--RUI-text-area-color': 'var(--RUI-color-warning)' },
    success: { '--RUI-text-area-color': 'var(--RUI-color-success)' },
}, { name: 'RUITextArea' });
var TextArea = function (_a) {
    var placeholder = _a.placeholder, value = _a.value, defaultValue = _a.defaultValue, onChange = _a.onChange, label = _a.label, className = _a.className, variant = _a.variant, color = _a.color, id = _a.id, style = _a.style, props = __rest(_a, ["placeholder", "value", "defaultValue", "onChange", "label", "className", "variant", "color", "id", "style"]);
    var theme = useTheme();
    var styles = useStyles(theme);
    var _b = useState(value || defaultValue || ''), innerValue = _b[0], setInnerValue = _b[1];
    // keeps innerValue updated with external one
    useEffect(function () {
        // never set value back to undefined to avoid controlled/uncontrolled error
        if (value !== undefined)
            setInnerValue(value);
    }, [value]);
    var handleTextAreaChange = function (event) {
        onChange && onChange(event);
        !value && setInnerValue(event.target.value);
    };
    var baseClasses = useMemoClasses([
        styles.base,
        color && styles[color],
        variant === 'filled' && styles.filled,
        theme.overrides.TextArea.base && styles.baseOverrides,
        variant === 'filled' && theme.overrides.TextArea.filled && styles.filledOverrides,
        'RUITextArea-base',
        className,
    ], [theme, className, variant, color]);
    var _c = useMultipleMemoClasses([
        [styles.textArea, theme.overrides.TextArea.textArea && styles.textAreaOverrides, 'RUITextArea-textArea'],
        [styles.label, theme.overrides.TextArea.label && styles.labelOverrides, 'RUITextArea-label'],
    ], [theme]), textAreaClasses = _c[0], labelClasses = _c[1];
    return (React.createElement("div", { className: baseClasses, style: style },
        React.createElement("textarea", __assign({}, props, { placeholder: placeholder, value: innerValue, onChange: handleTextAreaChange, className: textAreaClasses, id: id })),
        React.createElement("label", { className: labelClasses, htmlFor: id }, label)));
};
TextArea.defaultProps = {
    variant: 'standard',
    color: 'primary',
};
export { TextArea as default, TextArea };
